import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../../components/navbar';

const AdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [error, setError] = useState('');
  
  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch service details
  const fetchServiceDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/services/getAll`);
      const services = response.data;
      setServices(services);

      // Fetch user details for each service
      const userFetchPromises = services.map(async (service) => {
        // Handle userId whether it's a string or object
        let userId;
        if (typeof service.userId === 'string') {
          userId = service.userId;
        } else if (service.userId && typeof service.userId === 'object') {
          userId = service.userId._id;
        }
        
        if (userId && !userDetails[userId]) {
          try {
            const userResponse = await axios.get(`${API_URL}/api/admin/users/${userId}/get`);
            setUserDetails(prev => ({
              ...prev,
              [userId]: userResponse.data
            }));
          } catch (err) {
            console.error(`Error fetching user details for userId ${userId}:`, err);
          }
        }
      });

      await Promise.all(userFetchPromises);
    } catch (err) {
      setError('Error fetching service details');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch technicians
  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/technicians`);
      if (Array.isArray(response.data)) {
        setTechnicians(response.data);
      } else {
        setError('Technicians data is not in the expected format');
      }
    } catch (err) {
      setError('Error fetching technicians');
      console.error('Technician fetch error:', err);
    }
  };

  useEffect(() => {
    fetchServiceDetails();
    fetchTechnicians();
  }, []);

  // Calculate statistics based on selected month/year
  const calculateStats = () => {
    // Filter services for the selected month/year
    const monthlyServices = services.filter(service => {
      if (!service.createdAt) return false;
      const serviceDate = new Date(service.createdAt);
      return serviceDate.getMonth() === selectedMonth && 
             serviceDate.getFullYear() === selectedYear;
    });
    
    const totalServices = monthlyServices.length;
    const completedServices = monthlyServices.filter(s => s.status === 'Completed').length;
    const pendingServices = monthlyServices.filter(s => s.status === 'Pending').length;
    const inProgressServices = monthlyServices.filter(s => s.status === 'In Progress').length;
    
    // Technician stats
    const techStats = technicians.map(tech => {
      const techServices = monthlyServices.filter(s => 
        s.technicianId && (
          (typeof s.technicianId === 'string' && s.technicianId === tech._id) ||
          (typeof s.technicianId === 'object' && s.technicianId._id === tech._id)
      ))
      return {
        ...tech,
        assigned: techServices.length,
        completed: techServices.filter(s => s.status === 'Completed').length,
        pending: techServices.filter(s => s.status === 'Pending').length,
        completionRate: techServices.length > 0 
          ? Math.round((techServices.filter(s => s.status === 'Completed').length / techServices.length) * 100)
          : 0
      };
    });
    
    // Service type breakdown
    const serviceTypeStats = {};
    monthlyServices.forEach(service => {
      const type = service.inspectionType || 'Unknown';
      serviceTypeStats[type] = (serviceTypeStats[type] || 0) + 1;
    });
    
    return {
      totalServices,
      completedServices,
      pendingServices,
      inProgressServices,
      techStats,
      serviceTypeStats
    };
  };
  
  const stats = calculateStats();

  // Get month name from index
  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };

  // Generate year options
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 1, currentYear, currentYear + 1];
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Sort services by date for recent services
  const getRecentServices = () => {
    return [...services]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 10);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-28 flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
          <span className="ml-4 text-lg text-gray-700">Loading dashboard data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-28 p-6 max-w-7xl mx-auto">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error! </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="p-6 pt-28 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Monitor technician performance and service metrics</p>
          </div>
          
          <div className="flex flex-wrap gap-4 mt-4 md:mt-0">
            <div>
              <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-1">
                Month
              </label>
              <select
                id="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i}>
                    {getMonthName(i)}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
                Year
              </label>
              <select
                id="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                {getYearOptions().map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-md p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Services</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalServices}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Services in {getMonthName(selectedMonth)} {selectedYear}
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-md p-6 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Completed</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.completedServices}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Services completed this month
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl shadow-md p-6 border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">In Progress</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.inProgressServices}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Services currently in progress
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-md p-6 border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Pending</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.pendingServices}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Services awaiting assignment
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
          {/* Technician Performance */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-md overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">
                Technician Performance - {getMonthName(selectedMonth)} {selectedYear}
              </h2>
            </div>
            {stats.techStats.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Technician
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Completed
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Completion Rate
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pending
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.techStats.map((tech) => (
                      <tr key={tech._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                              {tech.firstname ? tech.firstname.charAt(0) : 'T'}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {tech.firstname} {tech.lastname}
                              </div>
                              <div className="text-sm text-gray-500">{tech.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{tech.assigned}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{tech.completed}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                              <div 
                                className="bg-blue-600 h-2.5 rounded-full" 
                                style={{ width: `${tech.completionRate}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium text-gray-700">{tech.completionRate}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            tech.pending > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {tech.pending}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No technician data</h3>
                <p className="mt-1 text-gray-500">No technician performance data available for this period.</p>
              </div>
            )}
          </div>
          
          {/* Service Breakdown */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">
                Service Breakdown
              </h2>
            </div>
            <div className="p-6">
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">By Status</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Completed</span>
                      <span className="text-sm font-medium text-gray-700">{stats.completedServices}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-green-600 h-2.5 rounded-full" 
                        style={{ width: `${(stats.completedServices / stats.totalServices) * 100 || 0}%` }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">In Progress</span>
                      <span className="text-sm font-medium text-gray-700">{stats.inProgressServices}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-yellow-500 h-2.5 rounded-full" 
                        style={{ width: `${(stats.inProgressServices / stats.totalServices) * 100 || 0}%` }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Pending</span>
                      <span className="text-sm font-medium text-gray-700">{stats.pendingServices}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-red-500 h-2.5 rounded-full" 
                        style={{ width: `${(stats.pendingServices / stats.totalServices) * 100 || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">By Type</h3>
                {Object.keys(stats.serviceTypeStats).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(stats.serviceTypeStats).map(([type, count]) => (
                      <div key={type}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{type}</span>
                          <span className="text-sm font-medium text-gray-700">{count}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className="bg-purple-600 h-2.5 rounded-full" 
                            style={{ width: `${(count / stats.totalServices) * 100 || 0}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No service type data available</p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Recent Services */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-10">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              Recent Services
            </h2>
          </div>
          {services.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Technician
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getRecentServices().map((service) => {
                    // Find technician for this service
                    const tech = technicians.find(t => {
                      if (!service.technicianId) return false;
                      
                      if (typeof service.technicianId === 'string') {
                        return t._id === service.technicianId;
                      }
                      
                      if (typeof service.technicianId === 'object') {
                        return t._id === service.technicianId._id;
                      }
                      
                      return false;
                    });
                    
                    return (
                      <tr key={service._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{service.customerName}</div>
                          <div className="text-sm text-gray-500">{service.customerPhone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{service.vehicleMake} {service.vehicleModel}</div>
                          <div className="text-sm text-gray-500">{service.variant}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {service.inspectionType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(service.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {tech ? `${tech.firstname} ${tech.lastname}` : 'Unassigned'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            service.status === 'Completed' 
                              ? 'bg-green-100 text-green-800' 
                              : service.status === 'In Progress'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {service.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No services found</h3>
              <p className="mt-1 text-gray-500">No services have been created yet.</p>
            </div>
          )}
        </div>
        
        {/* Performance Summary */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            Monthly Performance Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Completion Trend</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-end h-32">
                  <div className="flex-1 flex justify-around">
                    {[80, 65, 90, 75, 85, 95].map((value, index) => (
                      <div key={index} className="flex flex-col items-center mx-1">
                        <div 
                          className="w-6 bg-blue-500 rounded-t" 
                          style={{ height: `${value}%` }}
                        ></div>
                        <span className="text-xs text-gray-500 mt-1">{['J', 'F', 'M', 'A', 'M', 'J'][index]}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center mt-4 text-sm text-gray-600">Last 6 months completion rate</div>
              </div>
            </div> */}
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Performers</h3>
              {stats.techStats.filter(tech => tech.completed > 0).length > 0 ? (
                <div className="space-y-4">
                  {stats.techStats
                    .filter(tech => tech.completed > 0)
                    .sort((a, b) => b.completed - a.completed)
                    .slice(0, 3)
                    .map((tech, index) => (
                      <div key={tech._id} className="flex items-center">
                        <div className="bg-blue-100 w-8 h-8 rounded-full flex items-center justify-center text-blue-700 font-bold mr-3">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{tech.firstname} {tech.lastname}</div>
                          <div className="text-xs text-gray-500">{tech.completed} services completed</div>
                        </div>
                        <div className="text-sm font-bold text-green-600">{tech.completionRate}%</div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No technician performance data available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;